import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Download, Loader2, Lock, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fieldErrs } from "@/lib/api";
import { Field } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import { Switch } from "@/components/ui/switch";
import { AutoBackups } from "@/components/account/AutoBackups";

const BASE = process.env.REACT_APP_BACKEND_URL;
const ACTIVE = ["queued", "preparing"];
const REAUTH = "qhh.reauth";
export const FAIL = "We couldn't finish your export. Try again.";

export const fmtBytes = (n) => {
  if (!n) return "0 MB";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");

export async function downloadExport(id) {
  const { data } = await api.post(`/exports/${id}/link`);
  window.location.href = `${BASE}${data.url}`;
}

export function Confirm({ hasPassword, google, onDone, scope }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = useCallback(async (body) => {
    setBusy(true); setErr(null);
    try { onDone((await api.post("/exports/reauth", body)).data.reauth_token); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  }, [onDone]);
  useEffect(() => { if (google) send({ google_session_id: google }); }, [google, send]);
  const viaGoogle = () => {
    sessionStorage.setItem(REAUTH, `export:${scope}`);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.href.split("#")[0])}`;
  };
  if (!hasPassword) {
    return (
      <div className="space-y-3" data-testid="export-step-confirm">
        <p className="text-sm text-q-muted">For your security, sign in with Google again before we prepare your data.</p>
        {err && <p className="text-sm font-semibold text-red-300" role="alert" data-testid="export-reauth-error">{err}</p>}
        <button type="button" className="q-btn q-btn-secondary min-h-[44px] w-full" onClick={viaGoogle} disabled={busy} data-testid="export-google-btn">
          {busy ? "Checking…" : "Confirm with Google"}
        </button>
      </div>
    );
  }
  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); send({ password: pw }); }} data-testid="export-step-confirm">
      <p className="text-sm text-q-muted">For your security, confirm your password before we prepare your data.</p>
      <Field label="Your password" error={err}>
        <input type="password" autoFocus autoComplete="current-password" className="q-input min-h-[44px] w-full" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="export-password-input" />
      </Field>
      <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={!pw || busy} data-testid="export-confirm-btn">{busy ? "Checking…" : "Continue"}</button>
    </form>
  );
}

function Options({ opt, onStart, busy, errs }) {
  const [videos, setVideos] = useState(false);
  const [vault, setVault] = useState("");
  const [zpw, setZpw] = useState("");
  const [zpw2, setZpw2] = useState("");
  const sensitive = vault.length > 0;
  const mismatch = zpw && zpw2 && zpw !== zpw2;
  const ok = (!sensitive || (vault.length === 6 && zpw.length >= 8)) && (!zpw || (zpw.length >= 8 && zpw === zpw2));
  const total = opt.est_bytes + (videos ? opt.video_bytes : 0);
  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); onStart({ include_videos: videos, vault_code: vault || null, zip_password: zpw || null }); }} data-testid="export-step-options">
      <div>
        <p className="q-label mb-2">What's included</p>
        <ul className="grid max-h-48 grid-cols-1 gap-1 overflow-y-auto rounded-xl border border-q-line p-3 text-sm sm:grid-cols-2" data-testid="export-sections">
          {opt.sections.map((s) => (
            <li key={s.name} className="flex justify-between gap-2 text-q-text" data-testid={`export-section-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <span>{s.name}</span><span className="text-q-muted">{s.records}{s.files ? ` · ${s.files} files` : ""}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-sm text-q-muted">Estimated size <span className="font-semibold text-q-text" data-testid="export-est-size">{fmtBytes(total)}</span>. Includes sections that are turned off.</p>
      </div>
      <label className="flex items-center justify-between gap-3 rounded-xl border border-q-line p-3">
        <span>
          <span className="block font-semibold text-q-text">Include call videos</span>
          <span className="text-sm text-q-muted" data-testid="export-video-size">{opt.video_count ? `${opt.video_count} video${opt.video_count > 1 ? "s" : ""}, about ${fmtBytes(opt.video_bytes)}` : "No call videos"}</span>
        </span>
        <Switch checked={videos} onCheckedChange={setVideos} disabled={!opt.video_count} data-testid="export-videos-switch" />
      </label>
      {opt.sensitive_count > 0 && (
        <div className="rounded-xl border border-q-line p-3" data-testid="export-sensitive">
          <p className="flex items-center gap-2 font-semibold text-q-text"><Lock className="h-4 w-4 text-amber-300" /> Sensitive items ({opt.sensitive_count})</p>
          <p className="mb-2 text-sm text-q-muted">Sensitive Documents, policy numbers and "If something happens" sheets. Enter your vault code to include them, decrypted. Leave it empty to skip them.</p>
          {opt.has_vault_code
            ? <Field label="Vault code" error={errs?.vault_code}><input inputMode="numeric" maxLength={6} autoComplete="off" className="q-input min-h-[44px] w-full tracking-[0.4em]" value={vault} onChange={(e) => setVault(e.target.value.replace(/\D/g, ""))} data-testid="export-vault-input" /></Field>
            : <p className="text-sm text-q-muted" data-testid="export-no-vault">You haven't set a vault code, so sensitive items are left out.</p>}
        </div>
      )}
      <div className="rounded-xl border border-q-line p-3">
        <p className="flex items-center gap-2 font-semibold text-q-text"><ShieldCheck className="h-4 w-4 text-emerald-300" /> ZIP password {sensitive ? "(required)" : "(recommended)"}</p>
        <p className="mb-2 text-sm text-q-muted">AES-256. At least 8 characters. We don't keep it, so save it somewhere safe. Open the ZIP with 7-Zip, Keka or WinZip.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          <Field label="Password" error={errs?.zip_password}><input type="password" autoComplete="new-password" className="q-input min-h-[44px] w-full" value={zpw} onChange={(e) => setZpw(e.target.value)} data-testid="export-zip-password" /></Field>
          <Field label="Again" error={mismatch ? "The passwords don't match" : null}><input type="password" autoComplete="new-password" className="q-input min-h-[44px] w-full" value={zpw2} onChange={(e) => setZpw2(e.target.value)} data-testid="export-zip-password2" /></Field>
        </div>
      </div>
      {opt.left_out.length > 0 && (
        <details className="text-sm text-q-muted" data-testid="export-left-out">
          <summary className="cursor-pointer">Left out for privacy ({opt.left_out.reduce((a, r) => a + r.count, 0)})</summary>
          <ul className="mt-2 list-disc space-y-1 pl-5">{opt.left_out.map((r) => <li key={r.reason}>{r.reason}</li>)}</ul>
        </details>
      )}
      <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={!ok || busy} data-testid="export-start-btn">{busy ? "Starting…" : "Start export"}</button>
    </form>
  );
}

export function ExportProgress({ job, onAgain, onDeleted }) {
  const [busy, setBusy] = useState(false);
  if (ACTIVE.includes(job.status)) {
    return (
      <div className="space-y-3" data-testid="export-progress">
        <p className="flex items-center gap-2 font-semibold text-q-text"><Loader2 className="h-4 w-4 animate-spin" /> <span data-testid="export-progress-label">Preparing… {job.progress}%</span></p>
        <div className="h-2 overflow-hidden rounded-full bg-q-line"><div className="h-full rounded-full bg-[var(--glow-family)] transition-[width] duration-500" style={{ width: `${Math.max(3, job.progress)}%` }} /></div>
        <p className="text-sm text-q-muted">You can close this. We'll let you know in the app and by email when it's ready.</p>
      </div>
    );
  }
  if (job.status === "failed") {
    return (
      <div className="space-y-3" data-testid="export-failed">
        <p className="font-semibold text-red-300" role="alert" data-testid="export-failed-msg">{job.error || FAIL}</p>
        {onAgain && <button className="q-btn q-btn-primary min-h-[44px] w-full" onClick={onAgain} data-testid="export-try-again">Try again</button>}
      </div>
    );
  }
  if (job.status === "ready") {
    const dl = async () => { setBusy(true); try { await downloadExport(job.id); } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); } };
    const del = async () => { try { await api.delete(`/exports/${job.id}`); toast.success("Export deleted"); onDeleted?.(); } catch (x) { toast.error(errMsg(x)); } };
    return (
      <div className="space-y-3" data-testid="export-ready">
        <p className="flex items-center gap-2 font-semibold text-emerald-300"><CheckCircle2 className="h-4 w-4" /> Ready · {fmtBytes(job.size)}{job.encrypted ? " · password-protected" : ""}</p>
        <p className="text-sm text-q-muted" data-testid="export-expires">Available until {when(job.expires_at)}, then it's deleted automatically.</p>
        <div className="flex flex-wrap gap-2">
          <button className="q-btn q-btn-primary min-h-[44px]" onClick={dl} disabled={busy} data-testid="export-download-btn"><Download className="h-4 w-4" /> {busy ? "Starting…" : "Download ZIP"}</button>
          <button className="q-btn q-btn-ghost min-h-[44px]" onClick={del} data-testid="export-delete-btn"><Trash2 className="h-4 w-4" /> Delete now</button>
        </div>
      </div>
    );
  }
  return <p className="text-sm text-q-muted" data-testid="export-expired">This export has {job.status === "deleted" ? "been deleted" : "expired"}. You can make a new copy.</p>;
}

function ExportDialog({ scope, hub, hasPassword, google, onClose, initialJob }) {
  const [token, setToken] = useState(null);
  const [opt, setOpt] = useState(null);
  const [job, setJob] = useState(initialJob || null);
  const [busy, setBusy] = useState(false);
  const [errs, setErrs] = useState(null);
  const [err, setErr] = useState(null);
  const timer = useRef(null);
  const title = scope === "family" ? "Download all family data" : "Download my data";

  useEffect(() => {
    if (!token) return;
    api.get("/exports/options", { params: { scope, organization_id: hub.id } }).then(({ data }) => setOpt(data)).catch((x) => setErr(errMsg(x)));
  }, [token, scope, hub.id]);
  useEffect(() => {
    if (!job || !ACTIVE.includes(job.status)) return undefined;
    timer.current = setTimeout(() => api.get(`/exports/${job.id}`).then(({ data }) => setJob(data)).catch(() => {}), 1500);
    return () => clearTimeout(timer.current);
  }, [job]);

  const start = async (o) => {
    setBusy(true); setErrs(null); setErr(null);
    try {
      const { data } = await api.post("/exports", { scope, organization_id: hub.id, reauth_token: token, ...o });
      if (data.already) toast.message("An export is already being prepared, here's its progress");
      setJob(data);
    } catch (x) {
      const f = fieldErrs(x);
      if (f) setErrs(f); else setErr(errMsg(x));
      if (x?.response?.data?.detail?.code === "reauth_required") setToken(null);
    } finally { setBusy(false); }
  };

  let body;
  if (job) {
    body = <ExportProgress job={job} onAgain={() => { setJob(null); }} onDeleted={() => setJob(null)} />;
  } else if (!token) {
    body = <Confirm hasPassword={hasPassword} google={google} scope={scope} onDone={setToken} />;
  } else if (!opt) {
    body = err ? <p className="text-sm font-semibold text-red-300" role="alert" data-testid="export-error">{err}</p> : <p className="flex items-center gap-2 text-q-muted"><Loader2 className="h-4 w-4 animate-spin" /> Working out what's included…</p>;
  } else {
    body = (
      <>
        <Options opt={opt} onStart={start} busy={busy} errs={errs} />
        {err && <p className="mt-3 text-sm font-semibold text-red-300" role="alert" data-testid="export-error">{err}</p>}
      </>
    );
  }
  return (
    <FormDialog open onClose={onClose} title={title} testid="export-dialog"
      description={scope === "family" ? `Everything in ${hub.name}, as readable files plus every upload.` : "Your own data from every hub you're in."}>
      {body}
    </FormDialog>
  );
}

function Row({ scope, hub, hasPassword, google, label, hint, testid }) {
  const [open, setOpen] = useState(!!google);
  const [job, setJob] = useState(null);
  const load = useCallback(() => api.get("/exports/current", { params: { scope, organization_id: hub.id } }).then(({ data }) => setJob(data.export)).catch(() => {}), [scope, hub.id]);
  useEffect(() => { load(); }, [load, open]);
  const busy = job && ACTIVE.includes(job.status);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-q-line py-4 first:border-t-0" data-testid={`${testid}-row`}>
      <div className="min-w-0">
        <p className="font-semibold text-q-text">{label}</p>
        <p className="text-sm text-q-muted">{hint}</p>
        {job && <p className="mt-1 text-xs text-q-faint" data-testid={`${testid}-status`}>{busy ? `Preparing… ${job.progress}%` : job.status === "ready" ? `Ready until ${when(job.expires_at)}` : job.status === "failed" ? FAIL : `Last copy ${when(job.created_at)}`}</p>}
      </div>
      <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => setOpen(true)} data-testid={testid}><Download className="h-4 w-4" /> {busy ? "View progress" : job?.status === "ready" ? "Open" : "Download"}</button>
      {open && <ExportDialog scope={scope} hub={hub} hasPassword={hasPassword} google={google} onClose={() => { setOpen(false); load(); }}
        initialJob={job && (busy || job.status === "ready") ? job : null} />}
    </div>
  );
}

export default function YourData({ hub }) {
  const [info, setInfo] = useState(null);
  const [google, setGoogle] = useState({});
  useEffect(() => {
    const m = window.location.hash.match(/session_id=([^&]+)/);
    const r = sessionStorage.getItem(REAUTH);
    if (m && r?.startsWith("export:")) {
      sessionStorage.removeItem(REAUTH);
      setGoogle({ [r.slice(7)]: m[1] });
      window.history.replaceState(null, "", window.location.pathname);
    }
    Promise.all([api.get("/exports/eligibility", { params: { organization_id: hub.id } }), api.get("/me/account")])
      .then(([e, a]) => setInfo({ ...e.data, has_password: a.data.has_password })).catch(() => {});
  }, [hub.id]);
  if (!info || hub.view_mode !== "member") return null;
  return (
    <section id="your-data" className="q-card mt-6 p-4" data-testid="your-data">
      <h2 className="font-display text-lg font-bold text-q-text">Your data</h2>
      <p className="text-sm text-q-muted">One ZIP with every section as JSON, CSV and an offline page, plus your original files. The link works for 7 days.</p>
      <div className="mt-2">
        {info.family && <Row scope="family" hub={hub} hasPassword={info.has_password} google={google.family} label="Download all family data" testid="export-family-btn"
          hint="Parents only. Other adults' private pages stay out, and the other parents are told." />}
        {info.family && <AutoBackups hub={hub} hasPassword={info.has_password} google={google.backup} />}
        <Row scope="mine" hub={hub} hasPassword={info.has_password} google={google.mine} label="Download my data" testid="export-mine-btn"
          hint={hub.type === "roommates" ? "Your private space plus the shared sections. Never other roommates' private spaces." : "Your profile, messages you sent, your health page, sheet and uploads, from every hub you're in."} />
      </div>
    </section>
  );
}
